import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { useEffect, useState } from "react";
import { OrderHistoryType } from "@app/types";
import useObservable from "@core/hooks/use-observable.hook";
import OrderHistoryService from "@app/services/http/order.history.service";
import { formatDate, formatMoneyVN } from "@app/utils/util";
import Pagination from "../pagination";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES } from "@app/constants";
import { useT } from "@app/contexts/preferences.context";
import clsx from "clsx";
import { IcX } from "../icons";
import { iconBtn } from "../ui";

const UNIT: Record<string, string> = { hour: "Theo giờ", day: "Theo ngày", month: "Theo tháng" };

/** Lịch sử thuê (gia hạn) máy tính: OrderHistoryService.getOrderHistory như bản cũ */
function OrderHistoryModal({ portalDialogRef, portalData }: PortalDialogProps) {
  const t = useT();
  const order_id = portalData?.order_id;
  const [orderHistory, setOrderHistory] = useState<OrderHistoryType[]>([]);

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);

  const { subscribeUntilDestroy } = useObservable();

  const onPageChange = (page: number) => {
    setPage(page);
  };

  useEffect(() => {
    subscribeUntilDestroy(OrderHistoryService.getOrderHistory(order_id, page), (res) => {
      if (res.data.length) {
        setOrderHistory(res.data);
        setTotalPages(res.total_page);
      } else {
        setOrderHistory([]);
      }
    });
  }, [order_id, page]);

  const th = "whitespace-nowrap px-4 py-2.5 font-semibold";

  return (
    <Modal onCancel={portalDialogRef.close} buttonCancelInChildren={false}>
      <div className="flex max-h-[min(86vh,720px)] w-[min(680px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 pb-4 pt-5">
          <div className="min-w-0">
            <h2 className="text-[19px] font-bold">{t("Lịch sử thuê (gia hạn) máy tính")}</h2>
            <p className="m-0 mt-0.5 truncate font-mono text-[12.5px] text-muted">{order_id}</p>
          </div>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8")} aria-label={t("Đóng")} onClick={() => portalDialogRef.close()}>
            <IcX className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
          {!orderHistory.length ? (
            <p className="m-0 py-8 text-center text-muted">{t("Chưa có lịch sử")}</p>
          ) : (
            <table className="htbl w-full border-collapse overflow-hidden rounded-xl text-left text-[13px] tabular-nums">
              <thead>
                <tr className="bg-surface-2 text-xs text-muted">
                  <th className={th}>{t("Số giờ")}</th>
                  <th className={th}>{t("Đơn vị")}</th>
                  <th className={th}>{t("Số tiền")}</th>
                  <th className={th}>{t("Thời gian thực hiện")}</th>
                </tr>
              </thead>
              <tbody>
                {orderHistory.map((history) => (
                  <tr key={history._id} className="border-b border-line last:border-b-0">
                    <td data-label={t("Số giờ")} className="px-4 py-2.5 font-semibold">{history.rental_period}h</td>
                    <td data-label={t("Đơn vị")} className="px-4 py-2.5">{t(UNIT[history.unit] || "Theo tháng")}</td>
                    <td data-label={t("Số tiền")} className="px-4 py-2.5">{formatMoneyVN(history.price)}đ</td>
                    <td data-label={t("Thời gian thực hiện")} className="px-4 py-2.5">{formatDate(history.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center border-t border-line px-6 py-3">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default OrderHistoryModal;
